import React, { useEffect, useState } from "react";
import axios from 'axios';
import { useParams, Link } from "react-router-dom";
import CorporateHeader from "../../components/corporatedashboard/CorporateHeader";
import HealthWorkerDashboardSidebar from "../../components/global/HealthWorkerSidebar";
import { useStateProvider } from "../../manager/StateContext";
import { formatDates, DetailItem } from "../../utils/helpers";
import { FiUser, FiPhone, FiMail, FiCalendar, FiMapPin, FiFileText, FiActivity, FiSearch, FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import LoadingModal from "../../components/Modal/LoadingModal";

const HealthWorkerHomecareAppointmentView = () => {
  const [isOpen, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState("date");

  const [homecareData, setHomecareData] = useState(null);
  const [visitsData, setVisitsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const [{ stage_token }, dispatch] = useStateProvider();

  // Get contract id
  const { id: contractId } = useParams();
  const idNumber = parseInt(contractId.split('-')[1], 10);
 
  // Fetch Homecare Contracts 
  useEffect(() => {
    if (contractId) {
      FetchHomecareContractDetails();
      fetchVisitsData();
    }
  }, [contractId]);

  const FetchHomecareContractDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_LIVE_URL}/dashboard/homecare-booking/${contractId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );
      setHomecareData(response.data);
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setError("Failed to load appointment details. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Homecare Visits Data
  const fetchVisitsData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_LIVE_URL}/dashboard/homecare-appointment/visit/${idNumber}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );

      const visits = Array.isArray(response.data?.visitDetails) 
        ? response.data.visitDetails 
        : response.data?.visitDetails 
          ? [response.data.visitDetails] 
          : [];
      setVisitsData(visits);
      
    } catch (error) {
      console.error("Error fetching visits data:", error);
      // setError("Failed to load visit history. Please try again later.");
      setVisitsData([]); 
    } finally {
      setIsLoading(false);
    }
  };

  const assess = `/dashboard/homecare-appointment/assessment/${homecareData?.contractDetails?._id}`;

  const getStatusColor = (status) => {
    if (typeof status === 'number') {
      switch (status) {
        case 1: return "bg-green-100 text-green-800"; // Completed
        case 0: return "bg-blue-100 text-blue-800";   // Pending
        default: return "bg-gray-100 text-gray-800";  // Cancelled
      }
    }
    
    switch ((status || '').toLowerCase()) {
      case "completed": return "bg-green-100 text-green-800";
      case "pending": return "bg-blue-100 text-blue-500";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const sortTable = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };
  
  const sortedVisits = React.useMemo(() => {
    if(!Array.isArray(visitsData)) return [];
    if (!sortConfig.key) return [...visitsData];
  
    return [...visitsData].sort((a, b) => {
      // Handle numeric fields
      if (['visit_status', 'patient_paid', 'org_paid'].includes(sortConfig.key)) {
        if (sortConfig.direction === "ascending") {
          return (a[sortConfig.key] || 0) - (b[sortConfig.key] || 0);
        } else {
          return (b[sortConfig.key] || 0) - (a[sortConfig.key] || 0);
        }
      }
      
      // Handle date fields
      if (['visit_date', 'visit_end_date'].includes(sortConfig.key)) {
        const dateA = new Date(a[sortConfig.key] || 0);
        const dateB = new Date(b[sortConfig.key] || 0);
        if (sortConfig.direction === "ascending") {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      }
      
      // Default string comparison
      const valueA = String(a[sortConfig.key] || '').toLowerCase();
      const valueB = String(b[sortConfig.key] || '').toLowerCase();
      
      if (valueA < valueB) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }, [visitsData, sortConfig]);

  // Pagination logic
  const filteredVisits = sortedVisits
    .filter((visit) => {
      const healthWorkerName = visit.carer?.name || 
        `${visit.carer?.first_name || ''} ${visit.carer?.last_name || ''}`.trim();
      
      return (
        healthWorkerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (visit.visit_date || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        (visit.visit_end_date || '').toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    .filter((visit) => {
      if (!statusFilter) return true;
      const status = visit.visit_status;
      if (statusFilter === 'completed') return status === 1;
      if (statusFilter === 'pending') return status === 0;
      if (statusFilter === 'cancelled') return status === 2;
      return true;
    });
    
  const totalPages = Math.ceil(filteredVisits.length / itemsPerPage);
  const paginatedVisits = filteredVisits.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (isLoading && !homecareData) {
    return (
      <LoadingModal
      open={isLoading}
      setOpen={setIsLoading}
      title="We're fetching you Apointment details"
      className="z-50 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
    />
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex justify-between bg-[#F5F7FA] font-Montserrat h-screen">
      <HealthWorkerDashboardSidebar isOpen={isOpen} setOpen={setOpen} />
      
      <div className="sm:w-screen space-x-2 h-screen overflow-y-auto custom-scrollbar">
        <CorporateHeader isOpen={isOpen} setOpen={setOpen} />

        <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold text-[#2D3748]">Appointment Details</h1>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
              {/* Patient Card */}
              <div className="bg-white rounded-xl shadow-sm border border-[#EDF2F7] p-6 transition-transform hover:scale-[1.02]">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#EBF8FF] rounded-lg mr-3">
                    <FiUser className="text-[#3182CE] text-xl" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#2D3748]">Patient Information</h2>
                </div>
                <div className="space-y-3 capitalize">
                  <DetailItem icon={<FiUser />} label="Full Name" value={`${homecareData?.contractDetails?.patient?.first_name || 'Empty'} ${homecareData?.contractDetails?.patient?.last_name || ''}`.trim()} />
                  <DetailItem icon={<FiPhone />} label="Contact" value={homecareData?.contractDetails?.patient?.mobile || 'Empty'} />
                  <DetailItem icon={<FiMail />} label="Email" value={homecareData?.contractDetails?.patient?.email || 'Empty'} />
                  <DetailItem icon={<FiCalendar />} label="Health Description" value={homecareData?.contractDetails?.health_description || 'Empty'} />
                  <DetailItem icon={<FiMapPin />} label="Address" value={homecareData?.contractDetails?.patient?.address || 'Empty'} />
                </div>
              </div>

              {/* Contract Card */}
              <div className="bg-white rounded-xl shadow-sm border border-[#EDF2F7] p-6 transition-transform hover:scale-[1.02]">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#EBF4FF] rounded-lg mr-3">
                    <FiFileText className="text-[#667EEA] text-xl" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#2D3748]">Contract Details</h2>
                </div>
                <div className="space-y-3">
                  <DetailItem label="Contract Number" value={homecareData?.contractDetails?.contract_id || 'Empty'} />
                  <DetailItem label="Start Date" value={formatDates(homecareData?.contractDetails?.date || 'Empty')} />
                  <DetailItem
                      label="Consultation Price"
                      value={`₦${Number(homecareData?.contractDetails?.consultation_price || 0).toLocaleString()}`}
                    />
                  <DetailItem
                      icon={<FiFileText />}
                      label="Supporting Document"
                      value={
                        homecareData?.contractDetails?.upload ? (
                          <a
                            href={homecareData.contractDetails.upload}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#4299E1] font-medium hover:text-[#3182CE] underline"
                          >
                            View Document
                          </a>
                        ) : (
                          'Empty'
                        )
                      }
                    />

                  <div className="flex items-start">
                    <span className="text-sm font-medium text-[#718096] w-32">Payment Status</span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        homecareData?.contractDetails?.status === 1
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {homecareData?.contractDetails?.status === 1 ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  
                  <div className="flex items-start py-4">
                    <span className="text-sm font-medium text-[#718096] w-32">Assessment</span>
                   <span>
                    <Link to={assess} className="flex items-center px-4 py-[0.1rem] bg-[#4299E1] rounded-lg text-white hover:bg-[#3182CE] transition-colors">
                      View
                    </Link>
                   </span>
                  </div>
                </div>
              </div>

              {/* Service Card */}
              <div className="bg-white rounded-xl shadow-sm border border-[#EDF2F7] p-6 transition-transform hover:scale-[1.02]">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#F0FFF4] rounded-lg mr-3">
                    <FiActivity className="text-[#38A169] text-xl" />
                  </div>
                  <h2 className="text-xl font-semibold text-[#2D3748]">Service Details</h2>
                </div>
                <div className="space-y-3 capitalize">
                  <DetailItem label="Service Name" value={homecareData?.contractDetails?.service_type?.name || 'Empty'} />
                  <DetailItem label="Required Health Worker" value={homecareData?.contractDetails?.health_worker_type || 'Empty'} />
                  <DetailItem label="Category" value={homecareData?.contractDetails?.care_type || 'Empty'} />
                  <DetailItem label="Provider" value={homecareData?.contractDetails?.org_id?.name || 'Empty'} />
                </div>
              </div>
            </div>

            {/* Visits Table Section */}
            <div className="bg-white rounded-xl shadow-sm border border-[#EDF2F7] overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E2E8F0]">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <h2 className="text-xl font-semibold text-[#2D3748]">Visit History</h2>
                  
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A0AEC0]" />
                      <input
                        type="text"
                        placeholder="Search visits..."
                        className="pl-10 pr-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#4299E1] focus:border-[#4299E1] outline-none transition-all"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search visits"
                      />
                    </div>
                    
                    <select
                      className="px-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#4299E1] focus:border-[#4299E1] outline-none appearance-none bg-no-repeat bg-right pr-8"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23A0AEC0' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.75rem center' }}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      aria-label="Filter by status"
                    >
                      <option value="">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 space-y-2 sm:space-y-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-[#718096]">Show</span>
                    <select
                      className="px-2 py-1 border border-[#E2E8F0] rounded-md focus:ring-2 focus:ring-[#4299E1] focus:border-[#4299E1] outline-none"
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      aria-label="Items per page"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                    </select>
                    <span className="text-sm text-[#718096]">entries</span>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E2E8F0]">
                  <thead className="bg-[#4299E1]">
                    <tr>
                      {["S/N", "Health Worker", "Visit Start Date", "Visit End Date", "Client Payment Status", "Visit Status", "Carer Fee Status", "Report"].map((header, index) => {
                        const key = header.toLowerCase().replace(/\s+/g, '');
                        const isSorted = sortConfig.key === key;
                        const isAscending = isSorted && sortConfig.direction === 'ascending';
                        
                        return (
                          <th
                            key={header}
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-[#3182CE] transition-colors"
                            onClick={() => sortTable(key)}
                            aria-sort={isSorted ? (isAscending ? "ascending" : "descending") : "none"}
                          >
                            <div className="flex items-center">
                              {header}
                              {isSorted && (
                                <span className="ml-1">
                                  {isAscending ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                                </span>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E2E8F0]">
                    {paginatedVisits.length > 0 ? (
                      paginatedVisits.map((visit, index) => (
                        <tr key={visit._id} className="hover:bg-[#F7FAFC] transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#2D3748]">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                        
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4A5568] capitalize">
                            {visit.carer?.name || `${visit.carer?.first_name || ''} ${visit.carer?.last_name || ''}`.trim() || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4A5568]">
                            {formatDates(visit.visit_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4A5568]">
                            {formatDates(visit.visit_end_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              visit.patient_paid === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {visit.patient_paid === 1 ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              visit.visit_status === 1 ? 'bg-green-100 text-green-800' : 
                              visit.visit_status === 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {visit.visit_status === 1 ? 'Completed' : 
                              visit.visit_status === 0 ? 'Pending' : 'Cancelled'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              visit.org_paid === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {visit.org_paid === 1 ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-[#4A5568]">
                            <Link 
                              to={`/dashboard/homecare-appointment/visit/${visit._id}`}
                              className="text-[#4299E1] text-bold font-Poppins  hover:text-blue-800"
                            >
                              View 
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center font-Poppins text-lg text-gray-500">
                          No visits found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between">
                <div className="text-sm text-[#718096]">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredVisits.length)}</span> of{' '}
                  <span className="font-medium">{filteredVisits.length}</span> results
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md border ${currentPage === 1 ? 'border-[#E2E8F0] text-[#A0AEC0] cursor-not-allowed' : 'border-[#CBD5E0] text-[#4A5568] hover:bg-[#EDF2F7]'}`}
                    aria-label="Previous page"
                  >
                    <FiChevronLeft className="inline" />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-[#4299E1] text-white' : 'border border-[#CBD5E0] text-[#4A5568] hover:bg-[#EDF2F7]'}`}
                      aria-label={`Page ${page}`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md border ${currentPage === totalPages ? 'border-[#E2E8F0] text-[#A0AEC0] cursor-not-allowed' : 'border-[#CBD5E0] text-[#4A5568] hover:bg-[#EDF2F7]'}`}
                    aria-label="Next page"
                  >
                    <FiChevronRight className="inline" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthWorkerHomecareAppointmentView;